import { useEffect, useState } from 'react';
import api from '../api';
import { Type, Save } from 'lucide-react';

// Per-instance UI copy editor (Radly platform). Edits a curated set of i18n keys;
// empty = use the app default. The mobile app merges saved overrides over its
// bundled strings at launch. (The backend stores any key, so this list can grow.)
const CURATED = [
  {
    group: 'Tab labels',
    items: [
      { key: 'tabs.home', label: 'Home', def: 'Home' },
      { key: 'tabs.games', label: 'Games', def: 'Games' },
      { key: 'tabs.tournaments', label: 'Tournaments', def: 'Tourneys' },
      { key: 'tabs.promos', label: 'Rewards / Promos', def: 'Rewards' },
      { key: 'tabs.profile', label: 'Profile', def: 'Profile' },
    ],
  },
  {
    group: 'Home screen',
    items: [
      { key: 'home.welcome', label: 'Welcome greeting', def: 'Welcome back' },
      { key: 'home.pointsBalance', label: 'Points label', def: 'Reward Points' },
      { key: 'home.cashBalance', label: 'Cash balance label', def: 'Cash Balance' },
      { key: 'home.liveTables', label: 'Live tables label', def: 'Live Tables' },
      { key: 'home.playersClub', label: "Player's Club label", def: "Player's Club" },
      { key: 'home.quickActions', label: 'Quick actions header', def: 'Quick Actions' },
      { key: 'home.casinoInfo', label: 'Casino info label', def: 'Casino Info' },
    ],
  },
  {
    group: 'Games & Waitlist',
    items: [
      { key: 'games.title', label: 'Games screen title', def: 'Games' },
      { key: 'games.joinWaitlist', label: 'Join waitlist button', def: 'Join Waitlist' },
      { key: 'games.leaveWaitlist', label: 'Leave waitlist button', def: 'Leave Waitlist' },
      { key: 'games.onWaitlist', label: 'On waitlist label', def: 'On Waitlist' },
      { key: 'games.noGames', label: 'No games empty state', def: 'No games currently running' },
      { key: 'waitlist.title', label: 'My Waitlists title', def: 'My Waitlists' },
      { key: 'waitlist.seatReady', label: 'Seat ready banner', def: 'YOUR SEAT IS READY!' },
      { key: 'waitlist.youreBeingPaged', label: 'Being paged banner', def: "You're being paged!" },
      { key: 'waitlist.noWaitlists', label: 'No waitlists empty state', def: "You're not on any waitlists" },
    ],
  },
  {
    group: 'Rewards',
    items: [
      { key: 'promos.title', label: 'Rewards screen title', def: 'Rewards & Promos' },
      { key: 'promos.earnedRewards', label: 'Earned rewards header', def: 'Earned Rewards' },
      { key: 'promos.availableRewards', label: 'Available rewards header', def: 'Available Rewards' },
      { key: 'promos.noRewards', label: 'No rewards empty state', def: 'No rewards yet' },
      { key: 'promos.redeem', label: 'Redeem button', def: 'Redeem' },
      { key: 'promos.redeemInstructions', label: 'Redeem instructions', def: "Show this to the Player's Club desk to redeem" },
    ],
  },
  {
    group: 'Common buttons',
    items: [
      { key: 'common.loading', label: 'Loading', def: 'Loading...' },
      { key: 'common.retry', label: 'Retry', def: 'Retry' },
      { key: 'common.save', label: 'Save', def: 'Save' },
      { key: 'common.cancel', label: 'Cancel', def: 'Cancel' },
      { key: 'common.done', label: 'Done', def: 'Done' },
      { key: 'common.search', label: 'Search', def: 'Search' },
    ],
  },
];

export default function Content() {
  const [values, setValues] = useState({}); // key -> override string ('' = default)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/content');
      const o = res.data.overrides || {};
      const init = {};
      for (const g of CURATED) for (const it of g.items) init[it.key] = o[it.key] || '';
      setValues(init);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/admin/content', { overrides: values });
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Type className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-bold text-white">App Copy</h1>
      </div>
      <p className="text-gray-400 mb-6">
        Rename labels and copy shown in the mobile app. Leave a field blank to use the default.
        Changes apply on the next app launch.
      </p>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">{error}</div>}

      <div className="space-y-6">
        {CURATED.map((g) => (
          <div key={g.group} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/60">
              <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">{g.group}</h2>
            </div>
            <div className="divide-y divide-gray-700/60">
              {g.items.map((it) => {
                const custom = !!values[it.key];
                return (
                  <div key={it.key} className="flex items-center gap-4 px-4 py-3">
                    <div className="w-48 shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium">{it.label}</span>
                        {custom && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300">Custom</span>}
                      </div>
                      <div className="text-[11px] text-gray-500 font-mono">{it.key}</div>
                    </div>
                    <input
                      value={values[it.key] ?? ''}
                      placeholder={it.def}
                      onChange={(e) => setValues((s) => ({ ...s, [it.key]: e.target.value }))}
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="text-green-400 text-sm">Saved.</span>}
      </div>
    </div>
  );
}
